import { Fragment, isValidElement, useMemo } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CopyToClipboard, TWclassNames } from "../utils/Div";
import Breadcrumbs from "./breadcrumbs";
import { SeoHelmetWrapper, safeString } from "../Utils";

/**
 * DashboardHeader - Page header with title, breadcrumbs, actions and optional back button.
 *
 * Back button options:
 * - backUrl: Shorthand - pass a path string (e.g. "/dashboard/service/reports") for a simple back button.
 *   Uses replace: true to avoid polluting history (fixes multiple-clicks issue).
 * - backButton: Full config - { path, action, icon, label, useHistoryBack?, replace? }
 *   - useHistoryBack: When true, uses navigate(-1) for true browser back (single click).
 *   - replace: When true (default for path nav), replaces current history entry instead of pushing.
 */
export default function DashboardHeader(props) {
  const {
    title,
    subtitle,
    actions,
    meta,
    children,
    mainButton,
    backButton,
    backUrl,
    error,
    errors,
    compact,
    variant = "default",
    summaryItems = [],
    summaryFooter = null,
  } = props;
  const safeTitle = safeString(title, "");
  const safeSubtitle = safeString(subtitle, "");
  const isCompact = compact === true;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasError = error || (errors && errors.length > 0);
  const visibleActions =
    actions?.filter((action) => action.hidden === false && action.menu === false) || [];
  const menuActions =
    actions?.filter((action) => action.hidden === false && action.menu === true) || [];
  const visibleMeta = meta?.filter((item) => !item.hidden) || [];
  const visibleSummaryItems =
    summaryItems?.filter((item) => !item.hidden) || [];

  // Merge backUrl shorthand with backButton; backUrl takes precedence for path if both provided
  const resolvedBackButton = useMemo(() => {
    if (backUrl && !backButton) {
      return {
        path: backUrl,
        icon: ArrowLeftIcon,
        label: t("Back"),
        replace: true,
      };
    }
    if (backUrl && backButton && !backButton.path && !backButton.action) {
      return { ...backButton, path: backUrl, replace: backButton.replace !== false };
    }
    return backButton;
  }, [backUrl, backButton, t]);

  const handleBackClick = () => {
    if (!resolvedBackButton) return;
    if (resolvedBackButton.action) {
      resolvedBackButton.action();
      return;
    }
    if (resolvedBackButton.useHistoryBack) {
      navigate(-1);
      return;
    }
    if (resolvedBackButton?.path?.substring(0, 4) === "http") {
      window.location.replace(resolvedBackButton.path);
      return;
    }
    if (resolvedBackButton.path) {
      navigate(resolvedBackButton.path, { replace: resolvedBackButton.replace !== false });
    }
  };

  const getActionHandler = (action) => {
    if (action.disabled || action.loading) return undefined;
    if (action.action) return action.action;
    if (action?.path?.substring(0, 4) === "http") {
      return () => window.location.replace(action.path);
    }
    return () => navigate(action.path);
  };

  const renderActionIcon = (
    action,
    iconClassName = "-ml-0.5 mr-1.5 h-5 w-5 shrink-0 text-gray-400"
  ) => {
    if (action.loading) {
      return (
        <svg
          className={TWclassNames("animate-spin text-gray-400", iconClassName)}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      );
    }

    if (!action.icon) return null;

    return <action.icon className={iconClassName} aria-hidden="true" />;
  };

  const renderHeaderControls = (options = {}) => {
    const {
      gapClassName = "gap-2",
      buttonClassName = "inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300",
      menuButtonClassName = "inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400",
      wrapClassName = "flex flex-wrap items-center justify-end",
      mainButtonClassName = "inline-flex items-center rounded-md bg-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50",
      actionIconClassName = "-ml-0.5 mr-1.5 h-5 w-5 shrink-0 text-gray-400",
      mainIconClassName = "-ml-0.5 mr-1.5 h-5 w-5 shrink-0",
    } = options;

    return (
      <div className={TWclassNames(wrapClassName, gapClassName)}>
        {resolvedBackButton && (
          <button
            onClick={handleBackClick}
            type="button"
            className={TWclassNames(
              buttonClassName,
              "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <resolvedBackButton.icon
              className={actionIconClassName}
              aria-hidden="true"
            />
            {safeString(resolvedBackButton.label)}
          </button>
        )}

        {visibleActions.map((action, index) =>
          action.component ? (
            <div key={index}>{action.component}</div>
          ) : (
            <button
              key={index}
              onClick={getActionHandler(action)}
              type="button"
              disabled={action.disabled || action.loading}
              className={TWclassNames(
                buttonClassName,
                action.textColor ? action.textColor : "text-gray-900",
                action.bgColor ? action.bgColor : "bg-white",
                (action.disabled || action.loading)
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-gray-50"
              )}
            >
              {renderActionIcon(action, actionIconClassName)}
              {safeString(action.label)}
            </button>
          )
        )}

        {mainButton && (
          <button
            onClick={
              mainButton.action
                ? mainButton.action
                : mainButton?.path?.substring(0, 4) === "http"
                ? () => window.location.replace(mainButton.path)
                : () => navigate(mainButton.path)
            }
            type="button"
            disabled={mainButton.disabled}
            className={mainButtonClassName}
          >
            {mainButton.icon && (
              <mainButton.icon
                className={mainIconClassName}
                aria-hidden="true"
              />
            )}
            {safeString(mainButton.label)}
          </button>
        )}

        {menuActions.length > 0 && (
          <Menu as="div" className="relative w-full sm:w-auto">
            <Menu.Button className={menuButtonClassName}>
              More
              <ChevronDownIcon
                className="-mr-1 ml-1.5 h-6 w-6 shrink-0 text-gray-400 sm:h-5 sm:w-5"
                aria-hidden="true"
              />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 right-0 z-50 mt-2 max-h-[min(70vh,24rem)] min-w-0 origin-top overflow-y-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:left-auto sm:right-0 sm:w-48 sm:max-w-[min(12rem,calc(100vw-1.5rem-env(safe-area-inset-left)-env(safe-area-inset-right)))]">
                {menuActions.map((action, index) => (
                  <Menu.Item
                    key={index}
                    disabled={action.disabled || action.loading}
                  >
                    {({ disabled }) => (
                      <a
                        onClick={disabled ? undefined : getActionHandler(action)}
                        type="button"
                        className={TWclassNames(
                          action.textColor ? action.textColor : "text-gray-900",
                          action.bgColor ? action.bgColor : "bg-white",
                          "block min-h-12 px-4 py-3 text-base sm:min-h-0 sm:text-sm",
                          disabled
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer hover:bg-blue-100"
                        )}
                      >
                        <div className="flex items-center gap-1 sm:gap-0">
                          {renderActionIcon(action, actionIconClassName)}
                          {safeString(action.label)}
                        </div>
                      </a>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    );
  };

  const renderMetaChip = (item, index) => {
    const chipV2 = variant === "v2";
    const content = (
      <div
        className={TWclassNames(
          "inline-flex max-w-full items-center gap-2 rounded-full bg-gray-100 font-medium text-gray-600",
          chipV2
            ? "min-h-10 px-3 py-2 text-sm"
            : "px-2.5 py-1 text-xs"
        )}
      >
        {item.icon && (
          <item.icon
            className={TWclassNames(
              "shrink-0 text-gray-400",
              chipV2 ? "h-5 w-5" : "h-4 w-4"
            )}
            aria-hidden="true"
          />
        )}
        {item.component ? item.component : safeString(item.label)}
      </div>
    );

    if (item.action) {
      return (
        <button
          key={index}
          type="button"
          onClick={item.action}
          className={TWclassNames(
            chipV2 &&
              "min-h-11 w-full rounded-full text-left sm:w-auto sm:min-h-0"
          )}
        >
          {content}
        </button>
      );
    }

    if (item.path) {
      return (
        <Link
          key={index}
          to={item.path}
          className={TWclassNames(
            chipV2 &&
              "flex min-h-11 w-full items-center rounded-full sm:w-auto sm:min-h-0"
          )}
        >
          {content}
        </Link>
      );
    }

    return <div key={index}>{content}</div>;
  };

  const renderSummaryItemValue = (item) => {
    if (isValidElement(item.value)) {
      return item.value;
    }

    const interactiveClassName =
      variant === "v2"
        ? "flex min-h-11 w-full items-center text-left text-base text-blue-600 hover:text-blue-800 hover:underline sm:min-h-0 sm:w-auto sm:text-sm"
        : "text-left text-blue-600 hover:text-blue-800 hover:underline";

    if (item.action) {
      return (
        <button
          type="button"
          onClick={item.action}
          className={interactiveClassName}
        >
          {safeString(item.value)}
        </button>
      );
    }

    if (item.path) {
      return (
        <Link to={item.path} className={interactiveClassName}>
          {safeString(item.value)}
        </Link>
      );
    }

    return safeString(item.value, "—");
  };

  const v2Content = (
    <>
      <div className="bg-white">
        <Breadcrumbs currentPageTitle={safeTitle} />

        {!hasError && (
          <div className="mx-auto w-full px-3 py-4 sm:px-6 lg:max-w-7xl">
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                    <CopyToClipboard>{safeTitle}</CopyToClipboard>
                  </h2>
                  {safeSubtitle && (
                    <p className="mt-1 text-sm text-gray-500">
                      {safeSubtitle}
                    </p>
                  )}
                </div>

                {renderHeaderControls({
                  gapClassName: "gap-3",
                  wrapClassName:
                    "flex w-full flex-col sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end",
                  buttonClassName:
                    "inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-gray-200 px-4 py-3 text-base font-medium sm:min-h-0 sm:w-auto sm:px-3 sm:py-2 sm:text-sm",
                  menuButtonClassName:
                    "inline-flex min-h-11 w-full items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 sm:min-h-0 sm:w-auto sm:gap-0 sm:px-3 sm:py-2 sm:text-sm",
                  mainButtonClassName:
                    "inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-blue-400 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:w-auto sm:px-3 sm:py-2 sm:text-sm",
                  actionIconClassName:
                    "-ml-0.5 mr-2 h-6 w-6 shrink-0 text-gray-400 sm:mr-1.5 sm:h-5 sm:w-5",
                  mainIconClassName:
                    "-ml-0.5 mr-2 h-6 w-6 shrink-0 text-white sm:mr-1.5 sm:h-5 sm:w-5",
                })}
              </div>

              {(visibleMeta.length > 0 ||
                visibleSummaryItems.length > 0 ||
                summaryFooter) && (
                <div className="space-y-4 border-t border-gray-100 px-5 py-4">
                  {visibleMeta.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2">
                      {visibleMeta.map((item, index) =>
                        renderMetaChip(item, index)
                      )}
                    </div>
                  )}

                  {visibleSummaryItems.length > 0 && (
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      {visibleSummaryItems.map((item, index) => (
                        <div
                          key={index}
                          className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                        >
                          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {safeString(item.label)}
                          </dt>
                          <dd className="mt-1 break-words text-sm font-medium text-gray-900">
                            {renderSummaryItemValue(item)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {summaryFooter && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                      {summaryFooter}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={TWclassNames(
        isCompact ? "flex-1 min-h-0 flex flex-col overflow-hidden ml-0 sm:ml-4" : "mb-10 ml-0 sm:ml-20"
      )}>{children}</div>
    </>
  );

  const content = (
    <>
      <div className={TWclassNames("bg-white", isCompact && "flex-shrink-0")}>
        <Breadcrumbs currentPageTitle={safeTitle} />

        {!hasError && (
          <div className={TWclassNames(
            "mx-auto flex w-full items-center justify-between px-3 lg:flex lg:items-center lg:justify-between",
            isCompact ? "py-2 sm:px-4 lg:max-w-7xl" : "py-5 sm:px-20 lg:max-w-7xl"
          )}>
            <div className={TWclassNames("min-w-0 flex-1", isCompact ? "mb-1" : "mb-10")}>
              <h2 className={TWclassNames(
                "font-bold text-gray-900 sm:truncate",
                isCompact ? "mt-0 text-base sm:text-lg" : "mt-2 text-2xl leading-7 sm:text-3xl sm:tracking-tight"
              )}>
                <CopyToClipboard>{safeTitle}</CopyToClipboard>
              </h2>
              <p className={TWclassNames("text-sm text-gray-500", isCompact ? "mt-0.5" : "mt-1")}>{safeSubtitle}</p>
              {!isCompact && (
              <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                {meta?.map((meta, index) => {
                  if (!meta.hidden) {
                    return (
                      <Fragment key={index}>
                        {meta.action || meta.path ? (
                          <Link
                            to={meta.path}
                            className={TWclassNames(
                              meta.textColor ? meta.textColor : "text-gray-500",
                              "mt-2 flex items-center text-sm text-gray-500"
                            )}
                          >
                            {meta.component ? (
                              meta.component
                            ) : (
                              <>
                                {meta.icon && (
                                  <meta.icon
                                    className={TWclassNames(
                                      meta.iconColor
                                        ? meta.iconColor
                                        : "text-gray-400",
                                      "mr-1.5 h-5 w-5 flex-shrink-0"
                                    )}
                                    aria-hidden="true"
                                  />
                                )}
                                {isValidElement(meta.label)
                                  ? meta.label
                                  : safeString(meta.label)}
                              </>
                            )}
                          </Link>
                        ) : (
                          <div
                            className={TWclassNames(
                              meta.textColor ? meta.textColor : "text-gray-500",
                              "mt-2 flex items-center text-sm text-gray-500"
                            )}
                          >
                            {meta.component ? (
                              meta.component
                            ) : (
                              <>
                                {meta.icon && (
                                  <meta.icon
                                    className={TWclassNames(
                                      meta.iconColor
                                        ? meta.iconColor
                                        : "text-gray-400",
                                      "mr-1.5 h-5 w-5 flex-shrink-0"
                                    )}
                                    aria-hidden="true"
                                  />
                                )}
                                {isValidElement(meta.label)
                                  ? meta.label
                                  : safeString(meta.label)}
                              </>
                            )}
                          </div>
                        )}

                        {meta?.copy && !isValidElement(meta.label) ? (
                          <CopyToClipboard
                            type="button"
                            value={safeString(meta.label)}
                          />
                        ) : null}
                      </Fragment>
                    );
                  }
                })}
              </div>
              )}
            </div>
            <div className={TWclassNames("grid max-sm:justify-items-end gap-4 sm:gap-0 sm:flex lg:ml-4", isCompact ? "mt-2 lg:mt-0" : "mt-5 lg:mt-0")}>
              {/* Back Button - supports backUrl, backButton, useHistoryBack, replace */}
              {resolvedBackButton && (
                <span className="mr-3">
                  <button
                    onClick={handleBackClick}
                    type="button"
                    className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                  >
                    <resolvedBackButton.icon
                      className="-ml-0.5 mr-1.5 h-5 w-5"
                      aria-hidden="true"
                    />
                    {safeString(resolvedBackButton.label)}
                  </button>
                </span>
              )}

              {/* Map actions that is not hidden */}

              {actions &&
                actions
                  ?.filter(
                    (action) => action.hidden === false && action.menu === false
                  )
                  ?.map((action, index) => {
                    return (
                      <span
                        key={index}
                        className={TWclassNames(
                          action.menu ? "hidden" : "block",
                          "sm:block ml-3"
                        )}
                      >
                        {action?.component ? (
                          action?.component
                        ) : (
                          <button
                            onClick={
                              action.action
                                ? action.action
                                : action?.path?.substring(0, 4) === "http"
                                ? () => window.location.replace(action.path)
                                : () => navigate(action.path)
                            }
                            type="button"
                            disabled={action.disabled || action.loading}
                            className={TWclassNames(
                              action.textColor
                                ? action.textColor
                                : "text-gray-900",
                              action.bgColor ? action.bgColor : "bg-white",
                              "inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold  shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
                              (action.disabled || action.loading) ? "opacity-50 cursor-not-allowed" : ""
                            )}
                          >
                            {action.loading ? (
                              <svg className="animate-spin -ml-0.5 mr-1.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              action.icon && (
                                <action.icon
                                  className="-ml-0.5 mr-1.5 h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              )
                            )}
                            {safeString(action.label)}
                          </button>
                        )}
                      </span>
                    );
                  })}

              {/* Main button START */}
              {mainButton && (
                <span className="ml-3">
                  <button
                    onClick={
                      mainButton.action
                        ? mainButton.action
                        : mainButton?.path?.substring(0, 4) === "http"
                        ? () => window.location.replace(mainButton.path)
                        : () => navigate(mainButton.path)
                    }
                    type="button"
                    disabled={mainButton.disabled}
                    className="inline-flex items-center rounded-md bg-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {mainButton.icon && (
                      <mainButton.icon
                        className="-ml-0.5 mr-1.5 h-5 w-5"
                        aria-hidden="true"
                      />
                    )}
                    {safeString(mainButton.label)}
                  </button>
                </span>
              )}
              {/* Main button END */}

              {/* Dropdown */}
              {actions?.filter(
                (action) => action.hidden === false && action.menu === true
              ).length > 0 && (
                <Menu as="div" className="relative ml-3">
                  <Menu.Button className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400">
                    More
                    <ChevronDownIcon
                      className="-mr-1 ml-1.5 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-50 mt-2 w-48 max-w-[min(12rem,calc(100vw-1.5rem-env(safe-area-inset-left)-env(safe-area-inset-right)))] origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {actions &&
                        actions
                          ?.filter(
                            (action) =>
                              action.hidden === false && action.menu === true
                          )
                          ?.map((action, index) => {
                            return (
                              <Menu.Item key={index} disabled={action.disabled || action.loading}>
                                {({ disabled }) => (
                                  <a
                                    onClick={
                                      (action.disabled || action.loading) 
                                        ? undefined
                                        : action.action
                                        ? action.action
                                        : action?.path?.substring(0, 4) === "http"
                                        ? () =>
                                            window.location.replace(action.path)
                                        : () => navigate(action.path)
                                    }
                                    type="button"
                                    className={TWclassNames(
                                      action.textColor
                                        ? action.textColor
                                        : "text-gray-900",
                                      action.bgColor
                                        ? action.bgColor
                                        : "bg-white",
                                      "block text-sm py-4 px-4",
                                      (action.disabled || action.loading || disabled)
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-blue-100 cursor-pointer"
                                    )}
                                  >
                                    <div className="flex items-center">
                                      {action.loading ? (
                                        <svg className="animate-spin -ml-0.5 mr-1.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      ) : (
                                        action.icon && (
                                          <action.icon
                                            className="-ml-0.5 mr-1.5 h-5 w-5 text-gray-400"
                                            aria-hidden="true"
                                          />
                                        )
                                      )}
                                      {safeString(action.label)}
                                    </div>
                                  </a>
                                )}
                              </Menu.Item>
                            );
                          })}
                    </Menu.Items>
                  </Transition>
                </Menu>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={TWclassNames(
        isCompact ? "flex-1 min-h-0 flex flex-col overflow-hidden ml-0 sm:ml-4" : "mb-10 ml-0 sm:ml-20"
      )}>{children}</div>
    </>
  );

  if (isCompact) {
    return (
      <>
        <SeoHelmetWrapper title={safeTitle} description={safeSubtitle} subtitle={safeSubtitle} />
        <div className="flex flex-col h-screen min-h-0 overflow-hidden">
          {variant === "v2" ? v2Content : content}
        </div>
      </>
    );
  }

  return (
    <>
      <SeoHelmetWrapper title={safeTitle} description={safeSubtitle} subtitle={safeSubtitle} />
      {variant === "v2" ? v2Content : content}
    </>
  );
}
